import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

import { BudgetModal } from './BudgetModal.jsx';
import { BudgetCheckModal } from './BudgetCheckModal.jsx';

const MONTHS = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

export function BudgetsPage() {
  const { t } = useTranslation();
  const [year, setYear] = useState(2026);
  const [setOpen, setSetOpen] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);

  const list = useQuery({ queryKey: ['finance', 'budgets', year], queryFn: () => financeApi.listBudgets({ year }).then((r) => r.items) });
  const rows = (list.data || []).map((r) => ({ ...r, available: (Number(r.budgetedAmount) - Number(r.actualAmount)).toFixed(2) }));

  const totBudget = rows.reduce((s, r) => s + Number(r.budgetedAmount), 0);
  const totActual = rows.reduce((s, r) => s + Number(r.actualAmount), 0);
  const overCount = rows.filter((r) => Number(r.available) < 0).length;

  const columns = [
    { key: 'account', label: t('bud.col.account'), render: (r) => <span><span className="mono"><strong>{r.accountCode}</strong></span> {r.accountName}</span> },
    { key: 'period', label: t('bud.col.period'), render: (r) => `${MONTHS[r.month] || r.month} ${r.year}` },
    { key: 'budgeted', label: t('bud.col.budgeted'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.budgetedAmount)}</span> },
    { key: 'actual', label: t('bud.col.actual'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.actualAmount)}</span> },
    { key: 'available', label: t('bud.col.available'), align: 'right', render: (r) => <span className="mono num" style={{ color: Number(r.available) < 0 ? 'var(--color-danger, #dc2626)' : 'var(--color-success, #059669)' }}>{formatCurrency(r.available)}</span> },
    { key: 'state', label: '', render: (r) => (Number(r.available) < 0 ? <Badge variant="danger">{t('bud.over')}</Badge> : <Badge variant="success">{t('bud.within')}</Badge>) },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('bud.title')}</h1>
          <p className="muted">{t('bud.subtitle')}</p>
        </div>
        <div className="page-actions">
          <select className="input" style={{ maxWidth: 110 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button variant="ghost" onClick={() => setCheckOpen(true)}>{t('bud.action.check')}</Button>
          <Button variant="primary" onClick={() => setSetOpen(true)}>{t('bud.action.set')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('bud.metric.budgeted')} value={formatCurrency(totBudget)} />
        <MetricCard label={t('bud.metric.actual')} value={formatCurrency(totActual)} />
        <MetricCard label={t('bud.metric.over')} value={String(overCount)} trendVariant={overCount > 0 ? 'danger' : 'success'} />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('bud.empty')} />

      <BudgetModal open={setOpen} onClose={() => setSetOpen(false)} defaultYear={year} />
      <BudgetCheckModal open={checkOpen} onClose={() => setCheckOpen(false)} defaultYear={year} />
    </section>
  );
}
